import Box from '@/common/components/Box'
import TextField from '@/common/components/TextField'
import { FileUploader } from '@/common/components/FileUploader'
import { ErrorMessage } from '@/common/components/ErrorMessage'
import { getFilenameFromUrl } from '@/common/utils/getFilenameFromUrl'
import { TmaisBadgeFormSchema } from '../../schemas/form.schema'
import { UseFormReturn } from 'react-hook-form'
import { useEffect, useMemo, useState, useRef } from 'react'
import { HexColorPicker } from 'react-colorful'

const isValidHex = (value?: string | null) => /^#[0-9A-Fa-f]{6}$/.test(value || '')

type TmaisBadgeFormProps = {
  form: UseFormReturn<TmaisBadgeFormSchema>
  isSubmitting: boolean
  iconRequired?: boolean
  maxFileSizeInMb?: number
}

export const TmaisBadgeForm = ({
  form,
  isSubmitting = false,
  iconRequired = false,
  maxFileSizeInMb
}: TmaisBadgeFormProps) => {
  const {
    register,
    watch,
    setValue,
    clearErrors,
    setError,
    formState: { errors },
  } = form

  const watchIcon = watch('icon')
  const watchDisplayName = watch('display_name')
  const watchBackgroundColor = watch('background_color')
  const watchTextColor = watch('text_color')

  const [iconObjectUrl, setIconObjectUrl] = useState<string>()
  const [activePicker, setActivePicker] = useState<'bg' | 'text' | null>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setActivePicker(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (watchIcon instanceof File) {
      const objectUrl = URL.createObjectURL(watchIcon)
      setIconObjectUrl(objectUrl)
      return () => URL.revokeObjectURL(objectUrl)
    }
    setIconObjectUrl(undefined)
  }, [watchIcon])

  const previewUrl = useMemo(() => {
    if (watchIcon instanceof File) return iconObjectUrl
    if (typeof watchIcon === 'string') return watchIcon
    return undefined
  }, [watchIcon, iconObjectUrl])

  const previewFileName = useMemo(() => {
    if (watchIcon instanceof File) return watchIcon.name
    if (typeof watchIcon === 'string') return getFilenameFromUrl(watchIcon)
    return undefined
  }, [watchIcon])

  const previewBackground = isValidHex(watchBackgroundColor) ? watchBackgroundColor : '#1F2933'
  const previewColor = isValidHex(watchTextColor) ? watchTextColor : '#FFFFFF'

  const uploaderTitle = `Imagens/Gif do destaque 1:1 (mínimo de 64x64)${iconRequired ? ' *' : ''}`

  return (
    <Box className="flex flex-col gap-6 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] pb-8">
      <p className="text-lg font-bold">Detalhes do Destaque de item</p>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[240px,1fr] items-stretch">
        <div className="flex flex-col gap-2 h-full min-h-[160px]">
          {previewUrl && (
            <div className="relative w-full h-32 bg-neutral-op-8 rounded-md overflow-hidden shrink-0 border border-neutral-op-12 flex items-center justify-center">
              <img src={previewUrl} alt="Preview do ícone" className="max-w-full max-h-full object-contain" />
            </div>
          )}
          <div className="flex-1 w-full rounded-md transition-colors flex flex-col items-center justify-center
            [&>div]:w-full [&>div]:h-full [&>div]:m-0
            [&_label]:w-full [&_label]:h-full [&_label]:flex [&_label]:flex-col [&_label]:justify-center [&_label]:items-center [&_label]:m-0 [&_label]:bg-neutral-op-8 [&_label]:border-none
            [&_p]:text-center [&_span]:text-center
          ">
            <FileUploader
              title={uploaderTitle}
              name="icon"
              accept=".png,.jpg,.jpeg,.gif,image/png,image/jpeg,image/gif"
              currentFileUrl={previewUrl}
              fileName={previewFileName}
              maxFileSizeInMb={maxFileSizeInMb}
              handleChange={(file) => {
                setValue('icon', file, { shouldDirty: true, shouldValidate: true })
                clearErrors('icon')
              }}
              setError={(message) => {
                if (message) setError('icon', { message })
                else clearErrors('icon')
              }}
              disabled={isSubmitting}
            />
          </div>
          {errors.icon?.message && <ErrorMessage name="icon" message={errors.icon.message} />}
        </div>

        <div className="flex flex-col gap-5 justify-between">

          <TextField
            label="Nome do destaque (max 12 caracteres)"
            markAsRequired
            maxLength={12}
            placeholder="Ex: Coca-Cola Zero"
            {...register('display_name')}
            disabled={isSubmitting}
            errorMessage={errors.display_name?.message}
          />

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 relative">

            <div className="flex flex-col relative">
              <TextField
                label="Cor do background"
                markAsRequired
                maxLength={7}
                placeholder="#000000"
                {...register('background_color')}
                disabled={isSubmitting}
                errorMessage={errors.background_color?.message}
                LeftIcon={
                  <div
                    onClick={() => !isSubmitting && setActivePicker(activePicker === 'bg' ? null : 'bg')}
                    className="size-5 rounded-full border border-white/20 cursor-pointer hover:scale-110 transition-transform shadow-sm"
                    style={{ backgroundColor: previewBackground }}
                  />
                }
              />
              {activePicker === 'bg' && (
                <div ref={popoverRef} className="absolute top-[70px] left-0 z-50 p-3 bg-neutral-800 border border-white/10 rounded-xl shadow-2xl">
                  <HexColorPicker
                    color={previewBackground}
                    onChange={(color) => {
                      setValue('background_color', color.toUpperCase(), { shouldValidate: true, shouldDirty: true })
                      clearErrors('background_color')
                    }}
                  />
                </div>
              )}
            </div>

            <div className="flex flex-col relative">
              <TextField
                label="Cor da fonte"
                markAsRequired
                maxLength={7}
                placeholder="#FFFFFF"
                {...register('text_color')}
                disabled={isSubmitting}
                errorMessage={errors.text_color?.message}
                LeftIcon={
                  <div
                    onClick={() => !isSubmitting && setActivePicker(activePicker === 'text' ? null : 'text')}
                    className="size-5 rounded-full border border-white/20 cursor-pointer hover:scale-110 transition-transform shadow-sm"
                    style={{ backgroundColor: previewColor }}
                  />
                }
              />
              {activePicker === 'text' && (
                <div ref={popoverRef} className="absolute top-[70px] left-0 z-50 p-3 bg-neutral-800 border border-white/10 rounded-xl shadow-2xl">
                  <HexColorPicker
                    color={previewColor}
                    onChange={(color) => {
                      setValue('text_color', color.toUpperCase(), { shouldValidate: true, shouldDirty: true })
                      clearErrors('text_color')
                    }}
                  />
                </div>
              )}
            </div>

          </div>

          <div className="flex flex-row items-center gap-4 mt-2">
            <p className="text-base font-bold">Preview do app</p>
            <div
              className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 min-h-[32px] w-fit max-w-full overflow-hidden"
              style={{ backgroundColor: previewBackground, color: previewColor }}
            >
              {previewUrl ? (
                <img src={previewUrl} alt="Pré-visualização do ícone" className="size-4 object-contain" />
              ) : null}
              <span className="text-xs font-semibold whitespace-nowrap">
                {watchDisplayName?.length ? watchDisplayName : 'Nome do destaque'}
              </span>
            </div>
          </div>

        </div>
      </div>
    </Box>
  )
}
